import { useNavigate, useParams } from 'react-router-dom';

import { Box } from '@mui/material';

import { newPathFor } from '@/designs-dev/loft/projects';
import TabBar from '@/designs-dev/device/TabBar';
import { TabBarVisibilityProvider } from '@/designs-dev/device/TabBarVisibility';
import HomeScreen from '@/karehero-app/home/HomeScreen';
import ActionsScreen from '@/karehero-app/actions/ActionsScreen';
import { RECIPIENT } from '@/karehero-app/tell-us-once/intake/content';
import ProfileScreen from '@/karehero-app/care/ProfileScreen';
import ShortlistsTab from '@/karehero-app/care/shortlists/ShortlistsTab';
import HelpScreen from '@/karehero-app/help/HelpScreen';
import NotificationsScreen from '@/karehero-app/notifications/NotificationsScreen';
import TabPlaceholder from '@/karehero-app/shell/TabPlaceholder';
import ScreenRoot from '@/components/wrapped/ScreenRoot';

const TITLES: Record<string, string> = {
  settings: 'Settings',
  notifications: 'Notifications',
};

// The signed-in app shell: a tab's content above a persistent bottom nav.
//
// The tab is a prop now. It used to be read from a `:tab` param, which only
// worked while every tab was one URL segment under /karehero-app; the tabs are
// spread across the master prototype and two projects of their own now, so the
// route that mounts this knows which tab it is and the URL no longer has to
// spell it.
const MainTabs = ({ tab: tabProp }: { tab?: string }) => {
  const params = useParams();
  const tab = tabProp ?? params.tab ?? 'home';
  const navigate = useNavigate();

  const openNotifications = () =>
    navigate(newPathFor('/karehero-app/notifications') ?? '/');

  const content =
    tab === 'home' ? (
      <HomeScreen
        // Whose app this is. The bar named nobody, so every tab could have
        // belonged to anyone; the care recipient is the one thing every screen
        // under it is about.
        title={RECIPIENT}
        onNotifications={openNotifications}
        onFinancialStatus={() =>
          navigate(
            newPathFor('/karehero-app/forms/financial-assessment') ?? '/',
          )
        }
      />
    ) : tab === 'actions' ? (
      <ActionsScreen title={RECIPIENT} onNotifications={openNotifications} />
    ) : tab === 'profile' ? (
      <ProfileScreen onNotifications={openNotifications} />
    ) : tab === 'care' ? (
      <ShortlistsTab title={RECIPIENT} onNotifications={openNotifications} />
    ) : tab === 'help' ? (
      <HelpScreen title={RECIPIENT} onNotifications={openNotifications} />
    ) : tab === 'notifications' ? (
      <NotificationsScreen
        onBack={() => navigate(-1)}
        onNavigate={(path) => navigate(path)}
      />
    ) : (
      <TabPlaceholder title={TITLES[tab] ?? ''} />
    );

  return (
    <TabBarVisibilityProvider>
      {(navHidden) => (
        <ScreenRoot>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {content}
          </Box>
          {/* Collapses away while a sheet or pushed page is open. */}
          <Box
            sx={{
              maxHeight: navHidden ? 0 : 140,
              overflow: 'hidden',
              flexShrink: 0,
              transition: 'max-height 280ms ease',
            }}
          >
            {/* Tabs no longer share one URL shape: home, chat and settings are
                master screens, actions is a project of its own. The manifest is
                what knows, so the tab bar asks it. */}
            <TabBar
              active={tab}
              onSelect={(id) =>
                navigate(newPathFor(`/karehero-app/${id}`) ?? `/${id}`)
              }
            />
          </Box>
        </ScreenRoot>
      )}
    </TabBarVisibilityProvider>
  );
};

export default MainTabs;
